import React from "react";
import { Modal } from "../../../components/ui";
import { fmt, fmtDate } from "../../../lib/formatters";
import { FileText, Calendar, User, Package, RotateCcw, Store, DollarSign, History } from "lucide-react";

export const SaleReturnDetailModal = ({ saleReturn, onClose }) => {
  if (!saleReturn) return null;

  const customerName = typeof saleReturn.customer === 'object'
    ? (saleReturn.customer?.name || saleReturn.customer?.title || 'Unknown Customer')
    : (saleReturn.customer || saleReturn.party || 'Unknown Customer');

  const storeName = typeof saleReturn.store === 'object'
    ? (saleReturn.store?.storeName || saleReturn.store?.title || saleReturn.store?.name || 'Unknown Store')
    : (saleReturn.store || 'Unknown Store');

  return (
    <Modal
      isOpen={!!saleReturn}
      onClose={onClose}
      title={
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
            Verve Kitchens
          </span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
            Sale Return Details
          </span>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Information */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100">
              <RotateCcw size={24} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-slate-800 font-['Poppins']">
                {saleReturn.returnNo || `RET-${(saleReturn._id || '').toString().slice(-6).toUpperCase()}`}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[12px] font-medium text-slate-500">
                  Return Date: {saleReturn.date || saleReturn.createdAt ? fmtDate(saleReturn.date || saleReturn.createdAt) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div className="capitalize">
            {saleReturn.refundStatus === 'received' ? (
              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 uppercase tracking-tight">Fully Refunded</span>
            ) : saleReturn.refundStatus === 'partially_paid' ? (
              <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 uppercase tracking-tight">Partial</span>
            ) : (
              <span className="text-[10px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 uppercase tracking-tight">Pending</span>
            )}
          </div>
        </div>

        {/* Return Information Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <FileText size={14} />
              <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Sale Ref</span>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Invoice #</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">
                {saleReturn.originalInvoiceNo ||
                  saleReturn.invoiceNo ||
                  'N/A'}
              </p>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Customer</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">{customerName}</p>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Source</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">{storeName}</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              {saleReturn.refundStatus !== 'received' && <DollarSign size={14} />}
              <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Financial Summary</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Total Val</p>
                <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">{fmt(saleReturn.amount || saleReturn.totalAmount || 0)}</p>
              </div>
              <div>
                <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Refunded</p>
                <p className="text-[12px] font-medium text-emerald-600 truncate leading-snug">{fmt(saleReturn.refundAmount || saleReturn.receivedAmount || 0)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Balance Due</p>
                <p className="text-[12px] font-medium text-orange-600 truncate leading-snug">
                  {fmt(Math.max(0, (saleReturn.amount || saleReturn.totalAmount || 0) - (saleReturn.refundAmount || saleReturn.receivedAmount || 0)))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="p-5 rounded-2xl bg-slate-50/50 border border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-500">
              <History size={16} />
              <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Refund History</span>
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase">
              {saleReturn.payments?.length || 0} Transactions
            </div>
          </div>

          <div className="space-y-3">
            {saleReturn.payments && saleReturn.payments.length > 0 ? (
              saleReturn.payments.map((p, idx) => {
                const methodLabel = p.method?.replace('_', ' ').toUpperCase() || 'PAYMENT';
                return (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm hover:border-slate-200 transition-all">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-black px-2 py-0.5 rounded bg-slate-900 text-white uppercase tracking-wider">{methodLabel}</span>
                          <span className="text-[10px] font-bold text-slate-400">{fmtDate(p.date)}</span>
                        </div>
                        <p className="text-xl font-black text-slate-800 tracking-tighter">{fmt(p.amount)}</p>
                      </div>
                      {(p.transactionId || p.refId) && (
                        <div className="text-right">
                          <span className="text-[9px] font-black text-slate-400 uppercase block">Ref ID</span>
                          <span className="text-[11px] font-bold text-brand-blue">#{p.transactionId || p.refId}</span>
                        </div>
                      )}
                    </div>

                    {/* Dynamic Details based on method */}
                    <div className="grid grid-cols-2 gap-y-2 border-t border-slate-50 pt-3">
                      {p.bankName && (
                        <div>
                          <span className="text-[8px] font-black text-slate-400 uppercase block">Bank Name</span>
                          <span className="text-[10px] font-bold text-slate-700">{p.bankName} {p.branch ? `(${p.branch})` : ''}</span>
                        </div>
                      )}
                      {p.upiPhone && (
                        <div>
                          <span className="text-[8px] font-black text-slate-400 uppercase block">UPI Phone</span>
                          <span className="text-[10px] font-bold text-slate-700">{p.upiPhone}</span>
                        </div>
                      )}
                      {p.receiverName && (
                        <div>
                          <span className="text-[8px] font-black text-slate-400 uppercase block">Receiver</span>
                          <span className="text-[10px] font-bold text-slate-700">{p.receiverName}</span>
                        </div>
                      )}
                      {p.chequeNumber && (
                        <div>
                          <span className="text-[8px] font-black text-slate-400 uppercase block">Cheque No.</span>
                          <span className="text-[10px] font-bold text-slate-700">{p.chequeNumber}</span>
                        </div>
                      )}
                      {p.remarks && (
                        <div className="col-span-2 mt-1">
                          <span className="text-[8px] font-black text-slate-400 uppercase block">Remarks</span>
                          <span className="text-[10px] font-medium text-slate-500 italic">"{p.remarks}"</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-8 text-center bg-white rounded-xl border border-dashed border-slate-200">
                <History size={24} className="mx-auto text-slate-200 mb-2" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No transaction history found</p>
              </div>
            )}
          </div>
        </div>

        {/* Returned Items */}
        <div className="space-y-3">
          <h4 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase flex items-center gap-2">
            <Package size={14} className="text-slate-400" />
            Returned Items
          </h4>
          <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide">Item</th>
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide text-center">Qty</th>
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {saleReturn.items?.map((item, index) => {
                  const rate = item.rate || item.inventoryId?.purchaseRate || item.unitPrice || 0;
                  const qty = item.quantity || item.qty || 0;
                  const amount = item.amount || (rate * qty);

                  return (
                    <tr key={index} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">
                            {item.itemName || item.name || item.item?.name || item.item?.itemName || 'Unknown Item'}
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase tracking-tight">
                            {item.itemCode || item.item?.itemCode || item.inventoryId?.item?.itemCode || 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-[12px] font-medium text-slate-800">{qty}</span>
                        <span className="ml-1 text-[10px] text-slate-400 uppercase">{item.unit || item.item?.unit || item.inventoryId?.item?.units?.unitName || 'pcs'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-[12px] font-medium text-slate-800 truncate leading-snug">{fmt(amount)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <button
            className="flex-1 px-4 py-2.5 bg-slate-50 text-slate-600 font-medium text-[13px] rounded-lg border border-slate-200 hover:bg-slate-100 transition-all"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
